import { useMemo } from 'react'
import { prop } from 'lodash/fp'

export type LocalStateUpdater<TData = any, TValue = any> = (params: {
	control: TableChangesStorage<TData>
	row: TData
	name: string
	value: TValue
	defaultValue: TValue
}) => Record<string, object>

export type TableChangesStorage<TData = any> = {
	setLocalData: (localData: Record<string, object>) => void
	localData: Record<string, object>
	getId: (row: TData) => string
	reset: () => void
	getSnapshot: () => Record<string, object>
	isDirty: boolean
	updater: LocalStateUpdater
}
const defaultLocalStateUpdater: LocalStateUpdater = ({
	control,
	name,
	defaultValue,
	value,
	row,
}) => {
	const _id = control.getId(row)

	const newState = { ...control.localData } as Record<string, any>
	if (value === defaultValue) {
		if (name in newState[_id]) {
			delete newState[_id][name]
		}
		if (Object.keys(newState[_id]).length === 0) {
			delete newState[_id]
		}
	} else {
		newState[_id] = {
			...(newState[_id] || {}),
			[name]: value,
		}
	}

	return newState
}
/**
 * Hook for tracking changes in table cells
 */

export function useTableChangesStorage<TData = any>({
	setLocalData,
	localData,
	getId,
	updater = defaultLocalStateUpdater,
}: {
	setLocalData: (localData: Record<string, object>) => void
	localData: Record<string, object>
	getId: (row: TData) => string
	updater?: LocalStateUpdater
}): TableChangesStorage<TData> {
	const reset = () => setLocalData({})
	const getSnapshot = () => localData

	return {
		setLocalData,
		localData,
		getId,
		reset,
		getSnapshot,
		isDirty: Object.keys(localData).length > 0,
		updater,
	}
}

export function useTableChangesField<TValue = any, TData = any>({
	control,
	name,
	defaultValue,
	row,
}: {
	control: TableChangesStorage<TData>
	name: string
	defaultValue: TValue
	row: TData
}) {
	const _id = control.getId(row)
	const storedData = prop(_id, control.localData)
	/**
	 *  Store holds only user edits: no key for this field === cell untouched, use row default.
	 *  When the key exists, use the stored value (including null when user cleared the field).
	 */
	const value =
		storedData != null && name in storedData
			? (storedData as Record<string, TValue>)[name]
			: defaultValue

	return useMemo(
		() => ({
			value,
			onChange: (value: TValue) =>
				control.setLocalData(
					control.updater({
						control,
						name,
						defaultValue,
						value,
						row,
					})
				),
			isDirty: value !== defaultValue,
		}),
		[control, defaultValue, name, row]
	)
}
